import {
  Icon
} from "./chunk-7IEFGXFA.js";
import {
  cn
} from "./chunk-SBEJSA5Q.js";

// src/components/canvas/lightbox/InlineVideoPlayer.tsx
import { useCallback, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}
function InlineVideoPlayer({ src, filename, onExpand, className }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(true);
  const [loop, setLoop] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [started, setStarted] = useState(false);
  const togglePlay = useCallback((e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  }, []);
  const onScrub = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = Math.min(Math.max(0, (e.clientX - rect.left) / rect.width), 1) * duration;
  };
  const progress = duration > 0 ? time / duration * 100 : 0;
  const showBar = started && (hovered || !playing);
  const btn = "flex h-6 w-6 items-center justify-center rounded text-white/90 hover:bg-white/20 transition-colors";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("relative h-full w-full bg-black select-none", className),
      onMouseEnter: () => {
        setHovered(true);
        const v = videoRef.current;
        if (v && v.paused) void v.play().catch(() => {
        });
      },
      onMouseLeave: () => {
        setHovered(false);
        videoRef.current?.pause();
      },
      onClick: (e) => e.stopPropagation(),
      children: [
        /* @__PURE__ */ jsx(
          "video",
          {
            ref: videoRef,
            src,
            className: "h-full w-full object-contain",
            muted,
            loop,
            playsInline: true,
            preload: "metadata",
            onClick: togglePlay,
            onPlay: () => {
              setPlaying(true);
              setStarted(true);
            },
            onPause: () => setPlaying(false),
            onTimeUpdate: (e) => setTime(e.currentTarget.currentTime),
            onDurationChange: (e) => setDuration(e.currentTarget.duration || 0),
            "aria-label": filename
          }
        ),
        !playing && /* @__PURE__ */ jsx(
          "button",
          {
            className: "absolute inset-0 m-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/65 transition-colors",
            onClick: togglePlay,
            "aria-label": "Play",
            children: /* @__PURE__ */ jsx(Icon, { name: "play", className: "ml-0.5 h-5 w-5" })
          }
        ),
        onExpand && /* @__PURE__ */ jsx(
          "button",
          {
            className: cn("absolute top-1.5 right-1.5 rounded bg-black/50 p-1 text-white/90 hover:bg-black/70 transition-opacity", !showBar && "opacity-0"),
            onClick: (e) => {
              e.stopPropagation();
              videoRef.current?.pause();
              onExpand();
            },
            title: "Open large player",
            "aria-label": "Open large player",
            children: /* @__PURE__ */ jsx(Icon, { name: "maximize", className: "h-3.5 w-3.5" })
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: cn("absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-5 transition-opacity", !showBar && "opacity-0"), children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "group relative mb-1 h-2.5 cursor-pointer",
              onClick: onScrub,
              role: "slider",
              "aria-label": "Seek",
              "aria-valuemin": 0,
              "aria-valuemax": Math.round(duration),
              "aria-valuenow": Math.round(time),
              children: /* @__PURE__ */ jsx("div", { className: "absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-white/25 group-hover:h-1 transition-all", children: /* @__PURE__ */ jsx("div", { className: "h-full rounded-full bg-white", style: { width: `${progress}%` } }) })
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
            /* @__PURE__ */ jsx("button", { className: btn, onClick: togglePlay, title: playing ? "Pause" : "Play", "aria-label": playing ? "Pause" : "Play", children: /* @__PURE__ */ jsx(Icon, { name: playing ? "pause" : "play", className: "h-3.5 w-3.5" }) }),
            /* @__PURE__ */ jsxs("span", { className: "font-mono text-[11px] tabular-nums text-white/90", children: [
              formatTime(time),
              " / ",
              formatTime(duration)
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "ml-auto flex items-center gap-0.5", children: [
              /* @__PURE__ */ jsx("button", { className: cn(btn, loop && "bg-white/25"), onClick: (e) => {
                e.stopPropagation();
                setLoop((l) => !l);
              }, title: "Loop", "aria-pressed": loop, children: /* @__PURE__ */ jsx(Icon, { name: "repeat", className: "h-3.5 w-3.5" }) }),
              /* @__PURE__ */ jsx("button", { className: btn, onClick: (e) => {
                e.stopPropagation();
                setMuted((m) => !m);
              }, title: muted ? "Unmute" : "Mute", "aria-label": muted ? "Unmute" : "Mute", children: /* @__PURE__ */ jsx(Icon, { name: muted ? "volume-x" : "volume-2", className: "h-3.5 w-3.5" }) })
            ] })
          ] })
        ] })
      ]
    }
  );
}

export {
  InlineVideoPlayer
};
//# sourceMappingURL=chunk-X2UJPGLE.js.map